import {
  type Service,
  ServiceType,
} from '@wix/ambassador-bookings-services-v2-service/types';
import { BookingsSettings } from '@wix/ambassador-bookings-v2-bookings-settings/types';
import type { BusinessInfo } from '@wix/bookings-uou-types';
import type {
  ControllerFlowAPI,
  Experiments,
  IWixAPI,
} from '@wix/yoshi-flow-editor';
import type { BookingPolicyDto } from '../types/shared-types';
import { isCourseService } from '../utils/service';
import {
  type BodyViewModel,
  bodyViewModelFactory,
} from './body-view-model/bodyViewModel';
import {
  type BookButtonViewModel,
  bookButtonViewModelFactory,
} from './book-button-view-model/bookButtonViewModel';
import { BodyViewModelBuilder } from './builders/BodyViewModel.builder';
import { ContactSectionViewModelBuilder } from './builders/ContactSectionViewModel.builder';
import { DescriptionSectionViewModelBuilder } from './builders/DescriptionSectionViewModel.builder';
import { DetailsSectionViewModelBuilder } from './builders/DetailsSectionViewModel.builder';
import { GallerySectionViewModelBuilder } from './builders/GallerySectionViewModel.builder';
import { HeaderViewModelBuilder } from './builders/HeaderViewModel.builder';
import { PolicySectionViewModelBuilder } from './builders/PolicySectionViewModel.builder';
import { TitleAndTaglineViewModelBuilder } from './builders/TitleAndTaglineViewModel.builder';
import {
  type ContactSectionViewModel,
  contactSectionViewModelFactory,
} from './contact-section-view-model/contactSectionViewModel';
import {
  type DescriptionSectionViewModel,
  descriptionSectionViewModelFactory,
} from './description-section-view-modal/descriptionSectionViewModel';
import {
  CourseAvailabilityDisplay,
  type DetailsSectionViewModel,
  detailsSectionViewModelFactory,
} from './details-section-view-model/detailsSectionViewModel';
import {
  type DialogViewModel,
  dialogViewModelFactory,
} from './dialogViewModel/dialogViewModel';
import {
  type GallerySectionViewModel,
  gallerySectionViewModelFactory,
} from './gallery-section-view-model/gallerySectionViewModel';
import {
  type HeaderViewModel,
  headerViewModelFactory,
} from './header-view-model/headerViewModel';
import {
  type PolicySectionViewModel,
  policySectionViewModelFactory,
} from './policy-section-view-model/policySectionViewModel';
import {
  type TitleAndTaglineViewModel,
  titleAndTaglineViewModelFactory,
} from './title-and-tagline-view-model/titleAndTaglineViewModel';

export interface ServicePageViewModel {
  header: HeaderViewModel;
  body: BodyViewModel;
  titleAndTagline: TitleAndTaglineViewModel;
  detailsSection: DetailsSectionViewModel;
  contactSection: ContactSectionViewModel;
  descriptionSection: DescriptionSectionViewModel;
  policySection: PolicySectionViewModel;
  gallerySection: GallerySectionViewModel;
  showSchedulingSection: boolean;
  button?: BookButtonViewModel;
  navigationInitiatedBy?: string;
  dialogViewModel: DialogViewModel;
  waitingForServiceSlug?: boolean;
}

export const servicePageViewModelFactory = ({
  service,
  businessInfo,
  bookingsPolicy,
  t,
  experiments,
  viewTimezone,
  isSEO,
  settings,
  bookingsSettings,
  wixCodeApi,
}: {
  service: Service;
  businessInfo?: BusinessInfo;
  bookingsPolicy: BookingPolicyDto;
  t: ControllerFlowAPI['translations']['t'];
  isSEO?: boolean;
  viewTimezone?: string;
  experiments: Experiments;
  settings?: any;
  bookingsSettings?: BookingsSettings;
  wixCodeApi: IWixAPI;
}): ServicePageViewModel => {
  const body = bodyViewModelFactory({
    bookingsPolicy,
    service,
  });
  const isBookable = body.isBookable;
  const hideSchedulingSection =
    isCourseService(service) &&
    (!service.schedule?.lastSessionEnd ||
      service.schedule?.lastSessionEnd < new Date());
  const showSchedulingSection =
    service.type !== ServiceType.APPOINTMENT && !hideSchedulingSection;

  return {
    header: headerViewModelFactory(service, isBookable, isSEO),
    body,
    titleAndTagline: titleAndTaglineViewModelFactory(service),
    detailsSection: detailsSectionViewModelFactory({
      service,
      businessInfo,
      t,
      isBookable,
      viewTimezone,
      bookingsPolicy,
      settings,
    }),
    contactSection: contactSectionViewModelFactory({
      businessInfo,
      service,
    }),
    descriptionSection: descriptionSectionViewModelFactory(service, isBookable),
    policySection: policySectionViewModelFactory(bookingsPolicy),
    showSchedulingSection,
    button: bookButtonViewModelFactory(
      service,
      experiments,
      wixCodeApi,
      bookingsSettings,
    ),
    gallerySection: gallerySectionViewModelFactory(service),
    dialogViewModel: dialogViewModelFactory(),
  };
};

export const dummyViewModelFactory = ({
  t,
  isEditorX,
  courseAvailability,
  isWixStudio,
  isEditor3,
  waitingForServiceSlug,
}: {
  t: any;
  isEditorX: boolean;
  courseAvailability?: CourseAvailabilityDisplay;
  isWixStudio: boolean;
  isEditor3: boolean;
  waitingForServiceSlug?: boolean;
}): ServicePageViewModel => {
  const title = t('dummy-data.title');
  const tagline = t('dummy-data.tagline');
  const duration = t('dummy-data.duration');
  const price = t('dummy-data.price');
  const location = t('dummy-data.location');
  const description = t('dummy-data.description');
  const policy = t('dummy-data.policy');
  const phone = t('dummy-data.phone');
  const email = t('dummy-data.email');
  const address = t('dummy-data.address');
  const isBookable = true;
  const editor3Images = [
    '11e50e_7310d43c06d941cea0eb2710a99acffd~mv2.png',
    '11e50e_355e4b13c15843c3a28eeba3cc69ac4c~mv2.png',
    '11e50e_b42fbe2d430443b19d9f68dbb8bf2d03~mv2.png',
    '11e50e_de65721b4d4b42c8b8cff132b83ca3eb~mv2.png',
  ];
  const galleryEditorMap = {
    classic: [
      {
        fileName: '11062b_1e99942894b4427fabbd87b9e85cbf95~mv2.jpg',
        relativeUri: '11062b_1e99942894b4427fabbd87b9e85cbf95~mv2.jpg',
        width: 2606,
        height: 1745,
      },
      {
        fileName: '11062b_6ec3e1291f63411b84ce766a74e99608~mv2.jpg',
        relativeUri: '11062b_6ec3e1291f63411b84ce766a74e99608~mv2.jpg',
        width: 2606,
        height: 1745,
      },
      {
        fileName: '11062b_1c4adda107a544c5b89d0c069238eb9a~mv2.jpg',
        relativeUri: '11062b_1c4adda107a544c5b89d0c069238eb9a~mv2.jpg',
        width: 2606,
        height: 1745,
      },
      {
        fileName: '11062b_60d57732a78546119d2524ec64ad12df~mv2.jpg',
        relativeUri: '11062b_60d57732a78546119d2524ec64ad12df~mv2.jpg',
        width: 3648,
        height: 4209,
      },
    ],
    editorX: [
      {
        fileName: '11062b_1f94cea4fec64357b49a1880bebfd072~mv2.jpg',
        relativeUri: '11062b_1f94cea4fec64357b49a1880bebfd072~mv2.jpg',
        width: 2240,
        height: 1500,
      },
      {
        fileName: '11062b_a8466e6c581e4820b9c32c13e5a7dabb~mv2.jpg',
        relativeUri: '11062b_a8466e6c581e4820b9c32c13e5a7dabb~mv2.jpg',
        width: 2240,
        height: 1500,
      },
      {
        fileName: '11062b_df81c58f2799405ebec3bb5ebb7d6155~mv2.jpg',
        relativeUri: '11062b_df81c58f2799405ebec3bb5ebb7d6155~mv2.jpg',
        width: 2240,
        height: 1500,
      },
      {
        fileName: '11062b_a2e41b4d43ba4c78b7ca7375142a8f38~mv2.jpg',
        relativeUri: '11062b_a2e41b4d43ba4c78b7ca7375142a8f38~mv2.jpg',
        width: 3648,
        height: 4209,
      },
    ],
    studio: [
      {
        fileName: '2f543c_aec7bf888dd74b218db9495c52e2f030~mv2.jpg',
        relativeUri: '2f543c_aec7bf888dd74b218db9495c52e2f030~mv2.jpg',
        width: 2240,
        height: 1500,
      },
      {
        fileName: '2f543c_7ac5dc67e9cd4a52b52e0ee9ac9fe9ef~mv2.jpg',
        relativeUri: '2f543c_7ac5dc67e9cd4a52b52e0ee9ac9fe9ef~mv2.jpg',
        width: 2240,
        height: 1500,
      },
      {
        fileName: '2f543c_4afdfa84c505477b94d34441a73c5e87~mv2.jpg',
        relativeUri: '2f543c_4afdfa84c505477b94d34441a73c5e87~mv2.jpg',
        width: 2240,
        height: 1500,
      },
    ],
    editor3: [
      {
        fileName: editor3Images[0],
        relativeUri: editor3Images[0],
        width: 2240,
        height: 1500,
      },
      {
        fileName: editor3Images[1],
        relativeUri: editor3Images[1],
        width: 2240,
        height: 1500,
      },
      {
        fileName: editor3Images[2],
        relativeUri: editor3Images[2],
        width: 2240,
        height: 1500,
      },
      {
        fileName: editor3Images[3],
        relativeUri: editor3Images[3],
        width: 2240,
        height: 1500,
      },
    ],
  };

  const imageUriEditorMap = {
    classic: '11062b_7344b0fffa9c41e580794c15cea365d5~mv2.jpg',
    editorX: '11062b_7f7efcdb410c48c2a65be450de809052~mv2.jpg',
    studio: '2f543c_9849983df3bf4930a2e72e7accaac00f~mv2.jpg',
    editor3: '11e50e_67f0c8561b9d46a88b08e9795fa7990e~mv2.png',
  };
  const editorName = isEditor3
    ? 'editor3'
    : isWixStudio
    ? 'studio'
    : isEditorX
    ? 'editorX'
    : 'classic';

  const detailsSectionViewModelBuilder = new DetailsSectionViewModelBuilder()
    .withDuration(duration)
    .withPrice(price)
    .withLocation(location)
    .withBookOnlineAllowed(isBookable)
    .withFullyBooked(false);

  if (courseAvailability) {
    const numberOfSpotsLeft =
      courseAvailability === CourseAvailabilityDisplay.AVAILABILITY
        ? t('dummy-data.course-available')
        : t('dummy-data.number-of-spots-left');
    detailsSectionViewModelBuilder.withNumberOfSpotsLeft(numberOfSpotsLeft);
  }

  return {
    header: new HeaderViewModelBuilder()
      .withImage({
        relativeUri: imageUriEditorMap[editorName],
        height: 0,
        width: 0,
      })
      .withTitle(title)
      .withBookOnlineAllowed(isBookable)
      .build(),
    body: new BodyViewModelBuilder().withBookOnlineAllowed(isBookable).build(),
    titleAndTagline: new TitleAndTaglineViewModelBuilder()
      .withTitle(title)
      .withTagline(tagline)
      .withAvailableOnline(isBookable)
      .build(),
    detailsSection: detailsSectionViewModelBuilder.build(),
    contactSection: new ContactSectionViewModelBuilder()
      .withPhone(phone)
      .withEmail(email)
      .withAddress(address)
      .build(),
    descriptionSection: new DescriptionSectionViewModelBuilder()
      .withDescription(description)
      .withBookOnlineAllowed(isBookable)
      .build(),
    policySection: new PolicySectionViewModelBuilder()
      .withCancellationPolicy(policy)
      .build(),
    showSchedulingSection: true,
    button: { isPendingApprovalFlow: false },
    gallerySection: new GallerySectionViewModelBuilder()
      .withItems(galleryEditorMap[editorName])
      .build(),
    dialogViewModel: dialogViewModelFactory(),
    waitingForServiceSlug,
  };
};
